import { Close, DeleteOutlineRounded, Save } from '@mui/icons-material';
import * as Sentry from '@sentry/react';
import { Drawer, Modal } from "antd";
import Loading from "components/Loading";
import SelectTextEditorSingleWord from "components/UI/editor/selectTextEditorSingleWord.js";
import React, { useEffect, useMemo, useState, useRef } from "react";
import styled from "styled-components";
import COLORS from "utils/constants/color";
import { useAlert } from '../alertSakeBar/alertProvider';
import CustomButton from "../buttons";
import { InfoCircleOutlined } from '@ant-design/icons';
const SelectedTextEditor = ({
  text: optionText = "",
  submitHandler,
  open,
  onClose,
  highlightsWord: highlightsWordOption = [],
  answerSelected: answerSelectedOption = [],
  from ="",
  stripPunctuation = false,
  drawerZIndex,
}) => {
  const showAlert = useAlert();
  const [highlights, setHighlights] = useState(highlightsWordOption || []);
  const [loader, setLoader] = useState(false);
  const [highlightInfoModalOpen, setHighlightInfoModalOpen] = useState(false);
  const prevHighlightsRef = useRef();
  const prevOpenRef = useRef(open);

  const text = useMemo(() => optionText, [optionText]);
  const highlightsWord = useMemo(
    () => highlightsWordOption,
    [highlightsWordOption]
  );
  const answerSelected = useMemo(
    () => answerSelectedOption,
    [answerSelectedOption]
  );

  useEffect(() => {
    if (!open) {
      prevOpenRef.current = open;
      return;
    }

    const isOpening = !prevOpenRef.current && open;
    prevOpenRef.current = open;

    if (isOpening) {
      try {
        if (Array.isArray(highlightsWord)) {
          const data = highlightsWord.filter((x) => x.text !== "");
          setHighlights(data);
          prevHighlightsRef.current = JSON.stringify(data);
        }
      } catch (error) {
        Sentry.captureException(`392 Error during useEffect: ${error}`);
        console.log(error);
      }
      return;
    }

    const currentHighlightsStr = JSON.stringify(highlightsWord);
    if (prevHighlightsRef.current !== currentHighlightsStr) {
      try {
        if (Array.isArray(highlightsWord)) {
          const data = highlightsWord.filter((x) => x.text !== "");
          setHighlights(data);
          prevHighlightsRef.current = JSON.stringify(data);
        }
      } catch (error) {
        Sentry.captureException(`392 Error during useEffect: ${error}`);
        console.log(error);
      }
    }
  }, [open, highlightsWord]);

  const saveHandler = async () => {
    try {
      setLoader(true);
      if (highlights?.length === 0) {
        showAlert("Please select atleast one highlight", "error");
        return;
      }
      await submitHandler(highlights);
      onClose();
    } catch (error) {
      Sentry.captureException(`393 Error during saveHandler: ${error}`);
      console.log(error);
    } finally {
      setLoader(false);
    }
  };

  const removeHighlight = (index) => {
    const updatedHighlights = [...highlights];
    updatedHighlights.splice(index, 1);
    setHighlights(updatedHighlights);
  };

  const clearHighlight = () => {
    setHighlights([]);
  };

  return (
    <>
      <Drawer
        open={open}
        height="100%"
        placement="top"
        destroyOnHidden
        closable={false}
        zIndex={drawerZIndex}
      >
        <CloseBtn
          whileTap={{ scale: 0.98 }}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          onClick={() => {
            saveHandler();
          }}
        >
          <Close style={{ color: "white", fontWeight: "bold", fontSize: 20 }} />
        </CloseBtn>

        <Container>
          <TipMessage>
            <p>
              Click text to highlight important parts. Your highlights will
              appear below.
            </p>
          </TipMessage>

          <TextAreaWrapper
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -100 }}
            transition={{ type: "spring", stiffness: 300 }}
          >
            <div style={{ textAlign: "center", fontWeight: "bolder", fontSize: 16 }}>
              Select Highlight
                <span
                  role="button"
                  tabIndex={0}
                  onClick={() => setHighlightInfoModalOpen(true)}
                  onKeyDown={(e) => e.key === "Enter" && setHighlightInfoModalOpen(true)}
                  style={{ cursor: "pointer", marginLeft: 10, display: "inline-flex", alignItems: "center" }}
                >
                  <InfoCircleOutlined style={{ color: "black" }} />
                </span>
            </div>
            <Modal
              title="Instructions"
              open={highlightInfoModalOpen}
              onCancel={() => setHighlightInfoModalOpen(false)}
              footer={null}
              centered
            >
              <p style={{ margin: 0, fontSize: 14, lineHeight: 1.6 }}>
                <ul style={{ paddingLeft: '22px', margin: 0 }}>
                  <li>
                    Use <b>+</b> to combine words. The words will appear without a space on the student side.
                  </li>
                </ul>
                Example: <b>note+book</b> → <b>notebook</b>
              </p>
              <p style={{ margin: "12px 0 0", fontSize: 14, lineHeight: 1.6 }}>
                <ul style={{ paddingLeft: '22px', margin: 0 }}>
                  <li>
                    Use <b>_</b> to combine words. The words will appear with a space on the student side.
                  </li>
                </ul>
                Example: <b>high_school</b> → <b>high school</b>
              </p>
            </Modal>
            <div className="student-fill">
              <SelectTextEditorSingleWord
                highlights={highlights}
                initialText={text}
                setHighlights={setHighlights}
                from={from}
                stripPunctuation={stripPunctuation}
              />
            </div>
          </TextAreaWrapper>

          <ActionButtons>
            <CustomButton
              variant="destructive"
              disabled={highlights?.length === 0}
              onClick={clearHighlight}
            >
              <DeleteOutlineRounded size={16} />
              Clear All
            </CustomButton>
            <CustomButton
              variant="primary"
              onClick={saveHandler}
              disabled={loader}
            >
              {loader && <Loading />}
              <Save size={16} />
              Save Highlights
            </CustomButton>
          </ActionButtons>

          <HighlightsSection>
            {highlights && highlights?.length > 0 ? (
              <HighlightsList>
                <HighlightContainer>
                  {highlights?.map((highlight, idx) => (
                    <HighlightItem key={idx}>
                      <HighlightText>
                        {idx + 1}
                        {". "}
                        <span dangerouslySetInnerHTML={{ __html: highlight.text || '' }} />
                      </HighlightText>
                      <DeleteButton onClick={() => removeHighlight(idx)}>
                        <Close style={{ fontSize: 16 }} />
                      </DeleteButton>
                    </HighlightItem>
                  ))}
                </HighlightContainer>
              </HighlightsList>
            ) : (
              <EmptyHighlightItem>
                No selected highlight's available
              </EmptyHighlightItem>
            )}
          </HighlightsSection>
        </Container>
      </Drawer>
    </>
  );
};

export default SelectedTextEditor;

// Styled Components
const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;

  @media (min-width: 768px) {
    padding: 24px;
  }
`;

const TipMessage = styled.div`
  background-color: #e6f7ff;
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 16px;
  overflow: hidden; // To hide the overflow when the text moves

  width: 100%;
  display: flex;
  align-items: center;

  p {
    margin: 0;
    color: #1890ff;
    text-align: center;
    margin-inline: auto;
  }
`;

const TextAreaWrapper = styled.div`
  padding: 24px;
  margin-bottom: 24px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  cursor: text;
`;

const HighlightsSection = styled.div`
  h3 {
    font-size: 18px;
    font-weight: 600;
    margin-bottom: 16px;
  }
`;

const HighlightsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;
// Updated styled components
const HighlightContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  padding: 8px;
`;

const HighlightItem = styled.div`
  display: flex;
  align-items: center;
  background-color: #e0e0e0;
  border-radius: 20px;
  padding: 8px 12px;
  transition: background-color 0.2s ease;
  gap: 8px;
`;

const HighlightText = styled.span`
  font-size: 14px;
  color: #333;
  margin-right: 4px;
`;

const DeleteButton = styled.button`
  width: 24px;
  height: 24px;
  min-width: 24px;
  border-radius: 50%;
  background-color: ${COLORS.red};
  border: none;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${(props) => props.theme.colors?.redHover || "#ff7875"};
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const EmptyHighlightItem = styled(HighlightItem)`
  color: black;
  justify-content: center;
`;

const ActionButtons = styled.div`
  margin-bottom: 16px;
  display: flex;
  gap: 16px;
  margin-top: 24px;
  justify-content: end;
`;

const CloseBtn = styled.button`
  border: none;
  cursor: pointer;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  width: 25px;
  height: 25px;
  border-radius: 50%;
  position: absolute;
  top: 0px;
  right: 0px;
  color: white;
  z-index: 1;
  margin-right: 16px;
  margin-top: 7px;
  background: ${COLORS.red};
`;
